/**
 * Hook for fetching current check-in status of a unit
 */

import { useState, useEffect, useCallback } from 'react';
import { getUnitCurrentStatus } from '@/lib/checkinApi';
import type { CheckinCurrentStatus } from '@/types/checkin';

interface UseUnitCheckinStatusResult {
  currentStatus: CheckinCurrentStatus | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useUnitCheckinStatus(unitId: string | undefined): UseUnitCheckinStatusResult {
  const [currentStatus, setCurrentStatus] = useState<CheckinCurrentStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!unitId) {
      setCurrentStatus(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const status = await getUnitCurrentStatus(unitId);
      setCurrentStatus(status);
    } catch (err) {
      // Not an error if no status exists - unit just hasn't been checked in yet
      if (err instanceof Error && err.message.includes('No check-in record found')) {
        setCurrentStatus(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch check-in status');
      }
    } finally {
      setIsLoading(false);
    }
  }, [unitId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    currentStatus,
    isLoading,
    error,
    refetch: fetchStatus,
  };
}
